import React from "react";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Legend
} from "recharts";
import { SimulationSummaryResponse } from "../types";

type Props = { data: SimulationSummaryResponse };

export default function NavChart({ data }: Props) {
    const cur = data.currency === "USD" ? "$" : "€";

    // Muutetaan aika UNIX-ms:ksi (UTC), jotta scale="time" toimii siististi.
    const chartData =
        (data.monthly ?? []).map(m => {
            const d = new Date(m.periodStart);
            // Normalisoi kuukauden alkuun ja UTC:hen -> estää DST/locale-heitot
            const utc = Date.UTC(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
            return {
                t: utc, // X-akselin aikaleima (ms)
                nav: m.endNav,
                marketValue: m.endMarketValue,
                loan: m.endLoan,
            };
        }) ?? [];

    const tMin = chartData[0]?.t ?? Date.now();
    const tMax = chartData[chartData.length - 1]?.t ?? Date.now();

    // Kvartaali-tickit (tammi/huhti/heinä/loka) UTC:na
    const quarterTicks = buildQuarterTicks(tMin, tMax);

    return (
        <div className="card p-3">
            <h5 className="text-secondary">NAV &amp; balances over time</h5>
            <ResponsiveContainer width="100%" height={340}>
                <LineChart
                    data={chartData}
                    margin={{ top: 16, right: 10, left: 10, bottom: 8 }}
                >
                    <CartesianGrid strokeDasharray="3 3" />

                    <XAxis
                        dataKey="t"
                        type="number"
                        scale="time"
                        domain={["dataMin", "dataMax"]}
                        ticks={quarterTicks}
                        tickFormatter={(ts: number) =>
                            new Intl.DateTimeFormat("en-GB", {
                                month: "short",
                                year: "2-digit",
                            }).format(new Date(ts))
                        }
                        tick={{ fontSize: 11 }}
                        minTickGap={18}
                        interval="preserveStartEnd"
                    />

                    <YAxis
                        tick={{ fontSize: 11 }}
                        tickFormatter={(v: number) => formatCompact(v, cur)}
                        width={33}
                    />

                    <Tooltip
                        formatter={(value: number) => [formatMoney(value, cur)]}
                        labelFormatter={(ts: number) =>
                            new Intl.DateTimeFormat("fi-FI", {
                                month: "2-digit",
                                year: "numeric",
                            }).format(new Date(ts))
                        }
                    />

                    <Legend />

                    <Line type="monotone" dataKey="nav" name="NAV" stroke="#4caf50" dot={false} />
                    <Line type="monotone" dataKey="marketValue" name="Market value" stroke="#2196f3" dot={false} />
                    <Line type="monotone" dataKey="loan" name="Debt" stroke="#f44336" dot={false} />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}

/* ---------- helpers ---------- */

function formatMoney(n: number, cur: string): string {
    const hasCents = Math.abs(n % 1) > 1e-6;
    return (
        new Intl.NumberFormat("fi-FI", {
            minimumFractionDigits: hasCents ? 2 : 0,
            maximumFractionDigits: hasCents ? 2 : 0,
        }).format(n) + " " + cur
    );
}

function formatCompact(n: number, cur: string): string {
    // Käytetään englannin compact-muotoa -> 1k / 1M / 1B (lyhyempi kuin fi-FI "1 milj.")
    return new Intl.NumberFormat("en", {
        notation: "compact",
        maximumFractionDigits: 1
    }).format(n) + cur;
}

/**
 * Luo kvartaali-tickit UTC:na (tammi/huhti/heinä/loka).
 */
function buildQuarterTicks(startMs: number, endMs: number): number[] {
    if (!isFinite(startMs) || !isFinite(endMs) || startMs > endMs) return [];
    const ticks: number[] = [];

    const d = new Date(startMs);
    // Nollaa päivät ja kellonaika
    const dUTC = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1, 0, 0, 0, 0));

    // Siirry seuraavaan kvartaali-kuukauteen (0,3,6,9)
    while (![0, 3, 6, 9].includes(dUTC.getUTCMonth())) {
        dUTC.setUTCMonth(dUTC.getUTCMonth() + 1);
    }

    // Lisää kvartaaleittain kunnes endMs
    while (dUTC.getTime() <= endMs) {
        ticks.push(dUTC.getTime());
        dUTC.setUTCMonth(dUTC.getUTCMonth() + 3);
    }

    // Varmista että alku/loppu on mukana jos puuttuu
    if (ticks.length === 0 || ticks[0] > startMs) ticks.unshift(startMs);
    if (ticks[ticks.length - 1] < endMs) ticks.push(endMs);

    return ticks;
}
